import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Dashboard as DashboardIcon, Apps, ConfirmationNumber, Storage, People, Assessment, DarkMode, LightMode, MenuBook, ExitToApp, Menu as MenuIcon, Settings } from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useThemeStore } from '../stores/themeStore';

const drawerWidth = 260;

export function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { darkMode, toggleDarkMode } = useThemeStore();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogoutClick = () => {
        setLogoutDialogOpen(true);
    };

    const handleLogoutClose = () => {
        setLogoutDialogOpen(false);
    };

    const handleLogoutConfirm = async () => {
        setLogoutDialogOpen(false);
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const userEmail = auth.currentUser?.email || 'support@mightyscape.com';

    const menuItems = [
        { path: '/dashboard', icon: <DashboardIcon className="w-5 h-5" />, label: 'Overview' },
        { path: '/products', icon: <Apps className="w-5 h-5" />, label: 'Products' },
        { path: '/licenses', icon: <ConfirmationNumber className="w-5 h-5" />, label: 'Licenses' },
        { path: '/servers', icon: <Storage className="w-5 h-5" />, label: 'Machines' },
        { path: '/customers', icon: <People className="w-5 h-5" />, label: 'Customers' },
        { path: '/logs', icon: <Assessment className="w-5 h-5" />, label: 'Logs' },
        { path: '/settings', icon: <Settings className="w-5 h-5" />, label: 'Settings' },
        { path: '/docs', icon: <MenuBook className="w-5 h-5" />, label: 'Developer Docs' },
    ];

    // Shared Sidebar content designed like EPG Hub / Operations Console
    const sidebarContent = (
        <div className="flex flex-col h-full justify-between p-6 bg-[#0b1329] text-white rounded-3xl md:shadow-xl border border-slate-800/30">
            <div>
                {/* Header with Logo */}
                <div className="mb-8 pl-2">
                    <div className="text-[10px] tracking-[0.2em] text-[#c87a53] uppercase font-bold mb-1">
                        Operations Console
                    </div>
                    <div className="text-xs text-slate-400 font-normal leading-relaxed">
                        Admin session for<br />
                        <span className="font-semibold text-slate-300">{userEmail}</span>
                    </div>
                </div>

                {/* Nav Menu Items */}
                <nav className="space-y-1">
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setMobileOpen(false)}
                                className={`flex items-center gap-3.5 px-4 py-3 rounded-xl transition duration-150 text-sm font-medium ${
                                    isActive
                                        ? 'bg-white text-[#0b1329] shadow-md font-semibold'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800/40'
                                }`}
                            >
                                <span className={isActive ? 'text-[#0b1329]' : 'text-slate-400'}>
                                    {item.icon}
                                </span>
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-slate-800/60 space-y-3">
                <div className="flex justify-between items-center px-4">
                    <span className="text-xs text-slate-400 font-medium">Theme Mode</span>
                    <button
                        onClick={toggleDarkMode}
                        className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800/45 transition"
                    >
                        {darkMode ? <LightMode className="w-5 h-5 text-amber-400" /> : <DarkMode className="w-5 h-5" />}
                    </button>
                </div>
                <div className="text-[10px] text-center text-slate-500 uppercase tracking-widest pt-1">
                    Crafted by <span className="text-primary-500 font-bold">mightyscape</span>
                </div>
                <button
                    onClick={handleLogoutClick}
                    className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-950/20 transition duration-150 cursor-pointer"
                >
                    <ExitToApp className="w-5 h-5 text-red-400" />
                    <span>Sign Out</span>
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#faf7f0] dark:bg-[#070b13] flex p-0 md:p-5 gap-6 transition-colors duration-200">
            {/* Mobile Header Bar */}
            <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#faf7f0]/95 dark:bg-[#070b13]/95 backdrop-blur-md text-[#0b1329] dark:text-white border-b border-gray-200/60 dark:border-gray-800/60 z-20 flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        className="mr-1"
                    >
                        <MenuIcon />
                    </IconButton>
                </div>
                
                <IconButton onClick={toggleDarkMode} color="inherit">
                    {darkMode ? <LightMode className="w-5 h-5 text-amber-400" /> : <DarkMode className="w-5 h-5" />}
                </IconButton>
            </header>

            {/* Desktop Side Panel Wrapper */}
            <aside className="hidden md:block w-[260px] flex-shrink-0 h-[calc(100vh-40px)] sticky top-5">
                {sidebarContent}
            </aside>

            {/* Mobile Drawer Navigation overlay */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { 
                        boxSizing: 'border-box', 
                        width: drawerWidth,
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        padding: '12px'
                    },
                }}
            >
                {sidebarContent}
            </Drawer>

            {/* Main Content Area */}
            <main className="flex-1 min-w-0 px-4 md:px-2 pt-20 md:pt-4 overflow-y-auto">
                <Outlet />
            </main>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={handleLogoutClose}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
                PaperProps={{
                    className: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-2 max-w-sm shadow-xl'
                }}
            >
                <DialogTitle id="logout-dialog-title" className="font-bold text-gray-900 dark:text-white">
                    Confirm Sign Out
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description" className="text-gray-600 dark:text-gray-300 text-sm">
                        Are you sure you want to end your administration session and log out?
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="gap-2 p-4 pt-2">
                    <Button onClick={handleLogoutClose} className="text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 px-4 py-2 rounded-xl capitalize">
                        Cancel
                    </Button>
                    <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl capitalize shadow-sm">
                        Sign Out
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}